import { MGPFallible, MGPOptional, MGPValidation, ReversibleMap, Utils } from '@everyboard/lib';
import { GameNode } from '../../../jscaip/AI/GameNode';
import { Coord } from '../../../jscaip/Coord';
import { GameStatus } from '../../../jscaip/GameStatus';
import { Orthogonal } from '../../../jscaip/Orthogonal';
import { Player } from '../../../jscaip/Player';
import { PlayerNumberMap } from '../../../jscaip/PlayerMap';
import { ConfigurableRules } from '../../../jscaip/Rules';
import { TableUtils } from '../../../jscaip/TableUtils';
import { MancalaFailure } from './MancalaFailure';
import { MancalaMove } from './MancalaMove';
import { MancalaState } from './MancalaState';
export class MancalaNode extends GameNode {
}
export class MancalaRules extends ConfigurableRules {
    capturableValues;
    static FEED_ORIGINAL_HOUSE = () => $localize `Feed original house`;
    static MUST_FEED = () => $localize `You must feed`;
    static PASS_BY_PLAYER_STORE = () => $localize `Pass by player store`;
    static MULTIPLE_SOW = () => $localize `Continue distribution after last seed ends in store`;
    static CYCLICAL_LAP = () => $localize `Continue distribution until capture or empty house`;
    static SEEDS_BY_HOUSE = () => $localize `Seeds by house`;
    static NUMBER_OF_ROWS = () => $localize `Number of rows`;
    // These are the coordinates of the store. These are fake coordinates since the stores are not on the board
    static FAKE_STORE_COORD = new ReversibleMap([
        { key: Player.ZERO, value: new Coord(-1, -1) },
        { key: Player.ONE, value: new Coord(-1, +1) },
    ]);
    static isStarving(player, board, config) {
        return MancalaRules.getAllCoordOf(player, config).every((coord) => {
            return board[coord.y][coord.x] <= 0;
        });
    }
    static getAllCoordOf(player, config) {
        const coords = [];
        const y0 = player === Player.ZERO ? config.numberOfRows : 0;
        for (let y = 0; y < config.numberOfRows; y++) {
            for (let x = 0; x < config.width; x++) {
                coords.push(new Coord(x, y + y0));
            }
        }
        return coords;
    }
    static getEmptyDistributionResult(state) {
        return {
            capturedSum: 0,
            captureMap: TableUtils.create(state.getWidth(), state.getHeight(), 0),
            endsUpInStore: false,
            filledCoords: [],
            passedByStoreNTimes: 0,
            resultingState: state,
        };
    }
    static getInitialState(config) {
        const board = TableUtils.create(config.width, config.numberOfRows * 2, config.seedsByHouse);
        return new MancalaState(board, 0, PlayerNumberMap.of(0, 0));
    }
    constructor(capturableValues) {
        super();
        this.capturableValues = capturableValues;
    }
    isLegal(move, state, config) {
        let canStillPlay = true;
        for (const distribution of move) {
            Utils.assert(canStillPlay, 'Cannot play after non kalah move');
            const distributionResult = this.isLegalDistribution(distribution, state, config);
            if (distributionResult.isFailure()) {
                return MGPValidation.ofFallible(distributionResult);
            }
            else {
                const previousDistributionResult = MancalaRules.getEmptyDistributionResult(state);
                state =
                    this.distributeHouse(distribution, previousDistributionResult, config).resultingState;
                canStillPlay = distributionResult.get();
            }
        }
        if (config.mustContinueDistributionAfterStore) {
            Utils.assert(canStillPlay === false, 'Must continue playing after kalah move');
        }
        if (config.mustFeed) {
            const opponent = state.getCurrentOpponent();
            const opponentIsStarving = MancalaRules.isStarving(opponent, state.board, config);
            const playerDoesEmbargo = this.canDistribute(state.getCurrentPlayer(), state, config);
            if (opponentIsStarving && playerDoesEmbargo) {
                return MGPValidation.failure(MancalaFailure.SHOULD_DISTRIBUTE());
            }
        }
        return MGPValidation.SUCCESS;
    }
    getInitialState(config) {
        return MancalaRules.getInitialState(config);
    }
    /**
     * If the distribution is illegal, returns a failure including the failure reason
     * If the distribution is legal, return a MGPFallible of a boolean that is true if user can still play
     */
    isLegalDistribution(distribution, state, config) {
        if (state.getPieceAtXY(distribution.x, distribution.y) === 0) {
            return MGPFallible.failure(MancalaFailure.MUST_CHOOSE_NON_EMPTY_HOUSE());
        }
        const spaceOwner = this.getSpaceOwner(new Coord(distribution.x, distribution.y), config);
        if (spaceOwner === state.getCurrentOpponent()) {
            return MGPFallible.failure(MancalaFailure.MUST_DISTRIBUTE_YOUR_OWN_HOUSES());
        }
        const distributionResult = this.distributeMove(MancalaMove.of(distribution), state, config);
        const isStarving = MancalaRules.isStarving(distributionResult.resultingState.getCurrentPlayer(), distributionResult.resultingState.board, config);
        return MGPFallible.success(distributionResult.endsUpInStore && isStarving === false);
    }
    /**
     * Apply the distribution part of the move.
     * Apply the capture that happened due to distribution (for example the passage in the store).
     * Should not increment the turn of the state.
     */
    distributeMove(move, state, config) {
        const player = state.getCurrentPlayer();
        const filledCoords = [];
        let distributionResult = {
            capturedSum: 0,
            captureMap: TableUtils.create(config.width, config.numberOfRows * 2, 0),
            endsUpInStore: false,
            passedByStoreNTimes: 0,
            filledCoords: [],
            resultingState: state,
        };
        for (const distribution of move) {
            let houseToDistribute = new Coord(distribution.x, distribution.y);
            let mustDoOneMoreLap = true;
            while (mustDoOneMoreLap) {
                distributionResult =
                    this.distributeHouse(houseToDistribute, distributionResult, config);
                const captures = distributionResult.resultingState.getScoresCopy();
                captures.add(player, distributionResult.passedByStoreNTimes);
                filledCoords.push(...distributionResult.filledCoords);
                // distributionResult.passedByStoreNTimes += distributionResult.passedByStoreNTimes;
                houseToDistribute = distributionResult.filledCoords[distributionResult.filledCoords.length - 1];
                if (config.continueLapUntilCaptureOrEmptyHouse &&
                    distributionResult.endsUpInStore === false) {
                    mustDoOneMoreLap = this.isHouseCapturableOrEmpty(houseToDistribute, distributionResult.resultingState) === false;
                }
                else {
                    mustDoOneMoreLap = false;
                }
            }
        }
        return {
            endsUpInStore: distributionResult.endsUpInStore,
            filledCoords,
            passedByStoreNTimes: distributionResult.passedByStoreNTimes,
            resultingState: distributionResult.resultingState,
            capturedSum: 0,
            captureMap: distributionResult.captureMap,
        };
    }
    isHouseCapturableOrEmpty(coord, state) {
        const houseContent = state.getPieceAt(coord);
        return houseContent === 1 || this.capturableValues.some((value) => value === houseContent);
    }
    mustMonsoon(postCaptureState, config) {
        const postCaptureBoard = postCaptureState.getCopiedBoard();
        const opponent = postCaptureState.getCurrentOpponent();
        const player = postCaptureState.getCurrentPlayer();
        if (config.mustFeed) {
            if (MancalaRules.isStarving(player, postCaptureBoard, config) &&
                this.canDistribute(opponent, postCaptureState, config) === false) {
                // We are starving, and opponent can't feed us.
                // Opponent takes all their last piece for themselves
                return [opponent];
            }
        }
        else {
            if (MancalaRules.isStarving(opponent, postCaptureBoard, config)) {
                return [player];
            }
            else if (MancalaRules.isStarving(player, postCaptureBoard, config)) {
                return [opponent];
            }
        }
        return [];
    }
    getGameStatus(node, config) {
        const state = node.gameState;
        const width = node.gameState.getWidth();
        const seedsByHouse = config.seedsByHouse;
        const halfOfTotalSeeds = width * seedsByHouse * config.numberOfRows;
        if (state.scores.get(Player.ZERO) > halfOfTotalSeeds) {
            return GameStatus.ZERO_WON;
        }
        if (state.scores.get(Player.ONE) > halfOfTotalSeeds) {
            return GameStatus.ONE_WON;
        }
        if (state.scores.get(Player.ZERO) === halfOfTotalSeeds &&
            state.scores.get(Player.ONE) === halfOfTotalSeeds) {
            return GameStatus.DRAW;
        }
        return GameStatus.ONGOING;
    }
    applyLegalMove(move, state, config, _) {
        const distributionsResult = this.distributeMove(move, state, config);
        const captureResult = this.applyCapture(distributionsResult, config);
        let resultingState = captureResult.resultingState;
        const playerToMonsoon = this.mustMonsoon(resultingState, config);
        if (playerToMonsoon.length === 1) {
            // if the player distributed their last seeds and the opponent could not give them seeds
            const monsoonResult = this.monsoon(playerToMonsoon[0], captureResult);
            resultingState = monsoonResult.resultingState;
        }
        else if (playerToMonsoon.length === 2) {
            // if the player distributed their last seeds and the opponent could not give them seeds
            const monsoonResult = this.sharedMonsoon(captureResult);
            resultingState = monsoonResult.resultingState;
        }
        return new MancalaState(resultingState.board, resultingState.turn + 1, resultingState.scores);
    }
    /**
     * Simply distribute the group of seeds in (x, y)
     * Does not make the capture nor verify the legality of the move
     * Returns the coords of the filled houses
     */
    distributeHouse(distribution, previousLapResult, config) {
        let coord = new Coord(distribution.x, distribution.y);
        const initial = new Coord(distribution.x, distribution.y);
        let seedsInHand = previousLapResult.resultingState.getPieceAt(initial);
        let resultingState = previousLapResult.resultingState.setPieceAt(initial, 0);
        const player = resultingState.getCurrentPlayer();
        // to remember in order not to sow in the starting space if we make a full turn
        const filledCoords = [];
        let passedByStoreNTimes = 0;
        let previousDropWasStore = false;
        let endsUpInStore = false;
        let capturedSum = 0;
        while (seedsInHand > 0) {
            previousDropWasStore = endsUpInStore;
            endsUpInStore = false;
            const nextCoord = this.getNextCoord(coord, previousDropWasStore, previousLapResult.resultingState, config);
            endsUpInStore = nextCoord.isAbsent();
            if (endsUpInStore) {
                passedByStoreNTimes++;
                resultingState = resultingState.feedStore(player);
                seedsInHand--;
                filledCoords.push(MancalaRules.FAKE_STORE_COORD.get(player).get());
            }
            else {
                coord = nextCoord.get();
                if (initial.equals(coord) === false || config.feedOriginalHouse) {
                    // not to distribute on our starting space
                    const dropResult = this.getDropResult(seedsInHand, resultingState, coord);
                    resultingState = dropResult.resultingState;
                    previousLapResult.captureMap = TableUtils.add(previousLapResult.captureMap, dropResult.captureMap);
                    capturedSum += dropResult.capturedSum;
                    filledCoords.push(coord);
                    seedsInHand--; // drop in this space a piece we have in hand
                }
            }
        }
        return {
            filledCoords,
            passedByStoreNTimes,
            endsUpInStore,
            resultingState,
            capturedSum,
            captureMap: previousLapResult.captureMap,
        };
    }
    /**
     * @param config the config of the game
     * @param _seedsInHand the number of seed in hand at the drop moment
     * @param state the board on which a seed is going to be dropped
     * @param coord the coord to feed
     * @returns the result of the drop (updated)
     */
    getDropResult(_seedsInHand, state, coord) {
        return {
            capturedSum: 0,
            captureMap: TableUtils.create(state.getWidth(), state.getHeight(), 0),
            resultingState: state.feed(coord),
        };
    }
    getNextCoord(coord, previousDropWasStore, state, config) {
        const coordOwner = this.getSpaceOwner(coord, config);
        const horizontalDirection = coordOwner === Player.ONE ? Orthogonal.RIGHT : Orthogonal.LEFT;
        const nextCoord = coord.getNext(horizontalDirection);
        if (state.isOnBoard(nextCoord)) {
            return MGPOptional.of(nextCoord); // normal next
        }
        const isPlayerStore = state.getCurrentPlayer() === Player.ZERO ?
            nextCoord.x === -1 :
            nextCoord.x === config.width;
        if (config.passByPlayerStore && isPlayerStore && previousDropWasStore === false) {
            return MGPOptional.empty(); // This seed is dropped in the store
        }
        const newY = this.getOppositeY(coord, config);
        const newCoord = new Coord(coord.x, newY);
        return MGPOptional.of(newCoord); // switched sid
    }
    getOppositeY(coord, config) {
        const coordOwner = this.getSpaceOwner(coord, config);
        const verticalDirection = coordOwner === Player.ONE ? Orthogonal.DOWN : Orthogonal.UP;
        const verticalFactor = 2 * Math.abs((config.numberOfRows - 0.5) - coord.y);
        return coord.y + (verticalFactor * verticalDirection.y);
    }
    getStoreOwner(coord) {
        return MancalaRules.FAKE_STORE_COORD.reverse().get(coord);
    }
    getSpaceOwner(coord, config) {
        const owner = this.getStoreOwner(coord);
        if (owner.isPresent()) {
            return owner.get().getAnyElement().get(); // Only one player can be in there
        }
        else {
            return coord.y < config.numberOfRows ? Player.ONE : Player.ZERO;
        }
    }
    doesDistribute(x, y, state, config) {
        const board = state.board;
        let pieceNeededToFeedStore;
        if (y === 0) {
            pieceNeededToFeedStore = state.getWidth() - x;
        }
        else {
            pieceNeededToFeedStore = x + 1;
        }
        const storeOffset = config.passByPlayerStore ? 1 : 0;
        const pieceNeededToFeedOpponent = pieceNeededToFeedStore + storeOffset;
        return pieceNeededToFeedOpponent <= board[y][x]; // distribution from right to left
    }
    canDistribute(player, state, config) {
        for (const coord of MancalaRules.getAllCoordOf(player, config)) {
            if (this.doesDistribute(coord.x, coord.y, state, config)) {
                return true;
            }
        }
        return false;
    }
    /**
      * Captures all the seeds of the monsooning player.
      * Returns the sum of all captured seeds.
      * Is called when a game is over because of starvation
      * Or for Ba-awa/Adi: when we drop below 9 pieces
      */
    monsoon(monsooningPlayer, postCaptureResult) {
        const state = postCaptureResult.resultingState;
        const resultingBoard = TableUtils.create(state.getWidth(), state.getHeight(), 0);
        const captured = state.getScoresCopy();
        const capturedSum = state.getTotalRemainingSeeds();
        const captureMap = TableUtils.add(postCaptureResult.captureMap, state.board);
        captured.add(monsooningPlayer, capturedSum);
        return {
            capturedSum,
            captureMap,
            resultingState: new MancalaState(resultingBoard, state.turn, captured),
        };
    }
    sharedMonsoon(postCaptureResult) {
        const state = postCaptureResult.resultingState;
        const resultingBoard = TableUtils.create(state.getWidth(), state.getHeight(), 0);
        const captured = state.getScoresCopy();
        const capturedSum = state.getTotalRemainingSeeds();
        const captureMap = TableUtils.add(postCaptureResult.captureMap, state.board);
        captured.add(Player.ZERO, Math.floor(capturedSum / 2));
        captured.add(Player.ONE, Math.floor(capturedSum / 2));
        return {
            capturedSum,
            captureMap,
            resultingState: new MancalaState(resultingBoard, state.turn, captured),
        };
    }
    isCapturableValue(value) {
        return this.capturableValues.some((capturableValue) => capturableValue === value);
    }
}
